/**
 * DeviceInstallSheet — simplified bottom sheet for installation mode.
 * Big "Marcar como instalado" button; Edit/Replace as secondary actions.
 */
import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CheckCircle2, RefreshCw, MapPin, Calendar, RotateCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SUBTYPE_LBL = { gato_papel: "Gato papel", bandeja: "Bandeja", multicaptura: "Multicaptura", trex: "T-Rex", caja: "Cebadero de caja", tubo: "Cebadero de tubo" };
const TYPE_LBL = { cebadero_cebo: "Cebadero con cebo", trampa: "Trampa de captura", lampara_uv: "Lámpara UV" };
const ZONE_LBL = { verde: "Verde (perímetro exterior)", amarilla: "Amarilla (puntos focales interiores)", roja: "Roja (áreas de acceso al cliente)" };

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("es-SV", { day: "2-digit", month: "short", year: "numeric" });
    } catch (_) { return iso; }
}

export default function DeviceInstallSheet({ open, device, onClose, onUpdated }) {
    const [busy, setBusy] = useState(false);
    const [showReplace, setShowReplace] = useState(false);
    const [newQr, setNewQr] = useState("");

    React.useEffect(() => {
        if (open) {
            setShowReplace(false);
            setNewQr("");
        }
    }, [open, device?.id]);

    if (!device) return null;

    const isInstalled = !!device.installation_date;
    const subtype = device.trap_subtype ? SUBTYPE_LBL[device.trap_subtype] : "";

    const handleMarkInstalled = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/pest-devices/${device.id}/mark-installed`);
            toast.success(`${device.device_number} marcado como instalado`);
            onUpdated?.(r.data);
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const handleReplace = async () => {
        setBusy(true);
        try {
            const body = newQr.trim() ? { new_qr_code: newQr.trim() } : {};
            const r = await api.post(`/pest-devices/${device.id}/replace`, body);
            toast.success(`${r.data.device_number} reemplazado`);
            onUpdated?.(r.data);
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
            <SheetContent side="bottom" className="rounded-t-2xl max-h-[92vh] overflow-y-auto" data-testid="device-install-sheet">
                <SheetHeader className="text-left">
                    <SheetTitle className="text-2xl">{device.device_number}</SheetTitle>
                    <SheetDescription className="text-sm">
                        {TYPE_LBL[device.device_type]} · Zona {ZONE_LBL[device.zone]}
                        {device.device_type === "trampa" && (
                            <> · <span className={!subtype ? "italic" : ""}>{subtype || "Subtipo sin especificar"}</span></>
                        )}
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-4 space-y-3 pb-4">
                    {/* Location guidance */}
                    {device.area_name && (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-blue-700 flex-shrink-0" />
                            <div>
                                <div className="text-xs text-blue-700">Colocar en:</div>
                                <div className="font-semibold">{device.area_name}</div>
                            </div>
                        </div>
                    )}

                    {/* Status: installed or not */}
                    {isInstalled ? (
                        <div className="bg-green-50 border border-green-200 rounded-md p-3 space-y-1">
                            <div className="flex items-center gap-2 text-green-800 font-semibold">
                                <CheckCircle2 className="w-5 h-5" />
                                Ya instalado
                            </div>
                            <div className="text-xs text-green-700 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(device.installation_date)}
                            </div>
                            {device.replacement_count > 0 && (
                                <div className="text-xs text-green-700 flex items-center gap-1">
                                    <RotateCw className="w-3 h-3" />
                                    Reemplazado {device.replacement_count} {device.replacement_count === 1 ? "vez" : "veces"}
                                    {device.last_replacement_date && ` · último: ${formatDate(device.last_replacement_date)}`}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="bg-yellow-50 border border-yellow-300 rounded-md p-3 text-sm text-yellow-900">
                            Coloca el dispositivo físico en la ubicación indicada y luego confirma abajo.
                        </div>
                    )}

                    {/* Replace form */}
                    {showReplace && (
                        <div className="bg-slate-50 border rounded-md p-3 space-y-2">
                            <Label>Nuevo QR (opcional)</Label>
                            <Input
                                value={newQr}
                                onChange={(e) => setNewQr(e.target.value)}
                                placeholder="Dejar vacío para conservar el actual"
                                data-testid="install-replace-qr"
                            />
                            <p className="text-[11px] text-muted-foreground">
                                Solo escribe un código si el sticker físico se cambió. Si no, déjalo vacío.
                            </p>
                        </div>
                    )}
                </div>

                <SheetFooter className="sticky bottom-0 bg-white border-t pt-3 -mx-6 px-6 pb-4">
                    <div className="w-full space-y-2">
                        {!isInstalled && !showReplace && (
                            <Button onClick={handleMarkInstalled} disabled={busy} className="w-full h-12 text-base bg-green-600 hover:bg-green-700" data-testid="install-mark-btn">
                                <CheckCircle2 className="w-5 h-5 mr-2" /> Marcar como instalado
                            </Button>
                        )}
                        {showReplace && (
                            <Button onClick={handleReplace} disabled={busy} className="w-full h-12 text-base bg-orange-600 hover:bg-orange-700" data-testid="install-replace-confirm">
                                <RefreshCw className="w-5 h-5 mr-2" /> Confirmar reemplazo
                            </Button>
                        )}
                        <div className="flex gap-2">
                            {isInstalled && !showReplace && (
                                <Button variant="outline" onClick={() => setShowReplace(true)} className="flex-1" data-testid="install-replace-btn">
                                    <RefreshCw className="w-4 h-4 mr-1" /> Reemplazar
                                </Button>
                            )}
                            {showReplace && (
                                <Button variant="outline" onClick={() => setShowReplace(false)} className="flex-1">Cancelar reemplazo</Button>
                            )}
                            <Button variant="outline" onClick={onClose} className="flex-1">Cerrar</Button>
                        </div>
                    </div>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
